import { QI_FIELDS, QI_GROUPS, getQiManifest, type QiGroup } from './manifest';
import type {
  QiAggregationInput,
  QiAggregationResult,
  QiBaseline,
  QiExclusion,
  QiSubject,
  QiValue,
} from './types';
import { qiDraftExclusionComment } from './comment-identifiers';
import { qiDay, qiShiftDay, validateQiValues, validateQiCollectionMetadata } from './validate';

export const measuredQi = (value: number | string): QiValue => ({ state: 'measured', value });
export const missingQi = (reason = 'not_collected'): QiValue => ({
  state: 'not_collected',
  reason,
});
const stages = ['1', '2', '3', '4', 'unstageable', 'deep_tissue'] as const;
const roles = ['service_manager', 'rn', 'en', 'pcw'] as const;
const disciplines = [
  'physiotherapy',
  'occupational_therapy',
  'speech_pathology',
  'podiatry',
  'dietetics',
  'allied_health_assistant',
  'other',
] as const;
const modes = ['self', 'interviewer', 'proxy'] as const;
// Compare decimal weights exactly at the >=5% boundary, avoiding binary-float drift.
function significantLoss(previous: number, finish: number): boolean {
  const decimal = (v: number): [bigint, number] => {
    const [mantissa, exp = '0'] = String(v).toLowerCase().split('e');
    const [whole, fraction = ''] = mantissa!.split('.');
    return [BigInt(whole! + fraction), Number(exp) - fraction.length];
  };
  const [p, pe] = decimal(previous),
    [f, fe] = decimal(finish),
    scale = Math.min(pe, fe);
  return f * 100n * 10n ** BigInt(fe - scale) <= p * 95n * 10n ** BigInt(pe - scale);
}
const exclusions: Partial<Record<QiGroup, readonly QiExclusion[]>> = {
  PI: ['consent', 'absent'],
  PR: ['absent'],
  UPWL: ['consent', 'end_of_life', 'not_recorded'],
  FMI: ['absent'],
  MM: ['hospital'],
  ADL: ['end_of_life', 'absent', 'not_recorded'],
  IAD: ['absent'],
  HP: ['absent'],
  WF: [],
  CEI: ['absent', 'opted_out'],
  QOL: ['absent', 'opted_out'],
  AH: ['absent'],
};

/** Pure home × quarter aggregation. Manual A Sept 2026 §§7–20,23.
 * Assessed counts are eligible distinct people (T Section 1), with exclusions separate.
 * Missing observations poison only their subgroup; a measured empty population is zero.
 * No clinical narratives or subject ids are copied into output comments/issues.
 */
export function aggregateQi(input: QiAggregationInput): QiAggregationResult {
  const values: Record<string, QiValue> = {};
  const issues: QiAggregationResult['issues'] = [];
  const report = (
    code: string,
    path: string,
    category: 'INTEGRITY' | 'LEGAL' | 'POLICY' = 'INTEGRITY',
  ) => issues.push({ code, path, category });
  const fields = getQiManifest(input.programme);
  for (const f of fields) values[f.code] = missingQi();
  if (input.programme !== 'RESIDENTIAL')
    return {
      values,
      issues: [{ code: 'programme_not_available', path: 'programme', category: 'LEGAL' }],
    };
  if (
    !Number.isFinite(qiDay(input.periodStart)) ||
    !Number.isFinite(qiDay(input.periodEnd)) ||
    input.periodStart > input.periodEnd
  )
    return { values, issues: [{ code: 'invalid_period', path: 'period', category: 'INTEGRITY' }] };
  const baselineMap = new Map<string, QiBaseline>();
  for (const b of input.baselines) {
    const key = `${b.subjectId}|${b.subgroup}`;
    if (baselineMap.has(key)) report('duplicate_baseline', 'baselines');
    baselineMap.set(key, b);
  }
  const baseline = (s: QiSubject) => baselineMap.get(`${s.subjectId}|${s.subgroup}`);
  const set = (g: QiGroup, n: number, v: number | string) => {
    values[`${g}-${String(n).padStart(2, '0')}`] = measuredQi(v);
  };
  const provenance = (p: QiSubject['provenance'], path: string) => {
    if (p?.kind === 'prefill') {
      if (p.conformance === 'non_conforming')
        report('non_conforming_value_withheld', path, 'POLICY');
      else if (
        p.conformance === 'unknown' &&
        (!p.confirmed_by || !p.confirmed_at || !p.override_reason?.trim())
      )
        report('unknown_source_attestation_reason_required', path, 'POLICY');
      else if (!p.confirmed_by || !p.confirmed_at) {
        if (!p.override_reason?.trim())
          report('prefill_confirmation_override_required', path, 'POLICY');
      }
    }
  };
  for (const g of QI_GROUPS) {
    const group = input.groups[g];
    if (!group) continue;
    issues.push(
      ...validateQiCollectionMetadata(g, group.metadata ?? {}, input.periodStart, input.periodEnd),
    );
    const fs = QI_FIELDS.filter((f) => f.group === g);
    if (group.mode === 'AGGREGATE') {
      for (const [code, v] of Object.entries(group.values)) {
        if (!fs.some((f) => f.code === code)) {
          report('field_group_mismatch', g);
          continue;
        }
        if (
          ['PR-01', 'MM-01', 'MM-07'].includes(code) &&
          v.state === 'measured' &&
          typeof v.value === 'string' &&
          (qiDay(v.value) < qiDay(input.periodStart) + (code === 'MM-07' ? 7 : 0) ||
            qiDay(v.value) > qiDay(input.periodEnd))
        )
          report('invalid_collection_date', code);
        values[code] = v; // Never derive or default values in aggregate mode.
      }
      continue;
    }
    if (!group.collected || g === 'QIT') continue;
    const invalid = new Set<string>();
    const fail = (sub: string, code = 'incomplete_criterion') => {
      invalid.add(sub);
      report(code, `${g}.${sub}`);
    };
    for (const f of fs)
      values[f.code] =
        f.kind === 'count' ? measuredQi(0) : f.kind === 'comment' ? measuredQi('') : missingQi();
    const rejected = group.subjects.filter((s) => s.provenance?.conformance === 'non_conforming');
    for (const s of rejected) {
      invalid.add(g === 'WF' ? '*' : s.subgroup);
      report('non_conforming_value_withheld', g, 'POLICY');
    }
    const subjects = group.subjects.filter((s) => {
      if (s.provenance?.conformance === 'non_conforming') return false;
      if (s.group !== g) {
        report('subject_group_mismatch', g);
        invalid.add('*');
        return false;
      }
      return true;
    });
    const keys = new Set<string>();
    for (const s of subjects) {
      const key = `${s.subjectId}|${s.subgroup}|${s.discipline ?? ''}|${s.mode ?? ''}`;
      if (keys.has(key) && g !== 'AH') {
        report('duplicate_subject', g);
        invalid.add(s.subgroup);
      }
      keys.add(key);
      if (s.primaryExclusionReason && !exclusions[g]?.includes(s.primaryExclusionReason))
        fail(s.subgroup, 'invalid_exclusion');
      if (s.primaryExclusionReason === 'not_recorded' && !s.comment?.trim())
        report('missing_exclusion_comment', `${g}.${s.subgroup}`);
      provenance(s.provenance, g);
      if (
        s.provenance?.conformance === 'non_conforming' ||
        baseline(s)?.provenance?.conformance === 'non_conforming'
      )
        invalid.add(g === 'WF' ? '*' : s.subgroup);
    }
    const inPeriod = (date: string) =>
      Number.isFinite(qiDay(date)) && date >= input.periodStart && date <= input.periodEnd;
    const date = (value: string | undefined, n: number, sub: string) => {
      if (!value || !inPeriod(value)) {
        fail(sub, 'invalid_collection_date');
        return undefined;
      }
      set(g, n, value);
      return value;
    };
    const count = (ss: QiSubject[], n: number) =>
      set(g, n, new Set(ss.map((s) => s.subjectId)).size);
    const reasons = (ss: QiSubject[], codes: Partial<Record<QiExclusion, number>>) => {
      for (const [r, n] of Object.entries(codes))
        count(
          ss.filter((s) => s.primaryExclusionReason === r),
          n!,
        );
      return ss.filter((s) => !s.primaryExclusionReason);
    };
    if (g === 'PI') {
      const ss = subjects as QiSubject<'PI'>[];
      const eligible = reasons(ss, { consent: 2, absent: 3 }) as QiSubject<'PI'>[];
      count(eligible, 1);
      if (eligible.some((s) => !Array.isArray(s.criterion.injuries))) fail('pi');
      else {
        count(
          eligible.filter((s) => s.criterion.injuries.length > 0),
          4,
        );
        stages.forEach((stage, i) =>
          count(
            eligible.filter((s) => s.criterion.injuries.some((x) => x.stage === stage)),
            5 + i,
          ),
        );
        count(
          eligible.filter((s) => s.criterion.injuries.some((x) => x.acquiredOutsideThisQuarter)),
          11,
        );
        stages.forEach((stage, i) =>
          count(
            eligible.filter((s) =>
              s.criterion.injuries.some((x) => x.stage === stage && x.acquiredOutsideThisQuarter),
            ),
            12 + i,
          ),
        );
        if (
          eligible.some((s) =>
            s.criterion.injuries.some(
              (x) => !stages.includes(x.stage) || typeof x.acquiredOutsideThisQuarter !== 'boolean',
            ),
          )
        )
          fail('pi', 'invalid_injury');
      }
    } else if (g === 'PR') {
      const end = date(group.metadata?.collectionDate, 1, 'pr');
      const start = end ? qiShiftDay(end, -2) : undefined;
      if (
        (group.metadata?.windowStart && group.metadata.windowStart !== start) ||
        (group.metadata?.windowEnd && group.metadata.windowEnd !== end)
      )
        fail('pr', 'invalid_collection_window');
      const ss = subjects as QiSubject<'PR'>[];
      const es = reasons(ss, { absent: 3 }) as QiSubject<'PR'>[];
      count(es, 2);
      if (es.some((s) => !Array.isArray(s.criterion.events))) fail('pr');
      else {
        const qualifying = (s: QiSubject<'PR'>) =>
          s.criterion.events.filter(
            (e) => e.kind !== 'chemical' && start && end && e.date >= start && e.date <= end,
          );
        count(
          es.filter((s) => qualifying(s).length > 0),
          4,
        );
        count(
          es.filter(
            (s) =>
              qualifying(s).length > 0 && qualifying(s).every((e) => e.kind === 'secured_area'),
          ),
          5,
        );
        if (es.some((s) => s.criterion.events.some((e) => !Number.isFinite(qiDay(e.date)))))
          fail('pr', 'invalid_event_date');
      }
    } else if (g === 'UPWL') {
      for (const [sub, offset] of [
        ['significant', 0],
        ['consecutive', 7],
      ] as const) {
        // Manual §§10.3/11.3 and recording template T (UPWL G16/H16):
        // documented planned loss is outside this collection, not a reported exclusion.
        const ss = (subjects.filter((s) => s.subgroup === sub) as QiSubject<'UPWL'>[]).filter(
          (s) => s.criterion.plannedWeightLoss !== true,
        );
        if (subjects.some((s) => !['significant', 'consecutive'].includes(s.subgroup)))
          fail('*', 'invalid_subgroup');
        const derived = ss.map((s): QiSubject<'UPWL'> => {
          // An explicit primary exclusion needs no unavailable measurement criteria.
          if (s.primaryExclusionReason) return s;
          const b = baseline(s)?.previousFinishingWeight;
          const weights =
            sub === 'significant'
              ? [b, s.criterion.finishingWeight]
              : [
                  b,
                  s.criterion.startingWeight,
                  s.criterion.middleWeight,
                  s.criterion.finishingWeight,
                ];
          if (typeof s.criterion.plannedWeightLoss !== 'boolean') fail(sub);
          if (weights.some((w) => w !== null && w !== undefined && (!Number.isFinite(w) || w <= 0)))
            fail(sub, 'invalid_weight');
          const ex =
            s.primaryExclusionReason ??
            (weights.some((w) => w == null) ? 'not_recorded' : undefined);
          if (ex === 'not_recorded' && !s.comment?.trim())
            report('missing_exclusion_comment', `${g}.${sub}`);
          return { ...s, primaryExclusionReason: ex };
        });
        const es = reasons(derived, {
          consent: offset + 2,
          end_of_life: offset + 3,
          not_recorded: offset + 4,
        }) as QiSubject<'UPWL'>[];
        count(es, offset + 1);
        count(
          es.filter((s) => {
            const b = baseline(s)?.previousFinishingWeight;
            if (
              b == null ||
              s.criterion.finishingWeight == null ||
              !Number.isFinite(b) ||
              !Number.isFinite(s.criterion.finishingWeight) ||
              b <= 0 ||
              s.criterion.finishingWeight <= 0 ||
              s.criterion.plannedWeightLoss
            )
              return false;
            // Decimal arithmetic at the 5% boundary: 100*finish <= 95*previous.
            return sub === 'significant'
              ? significantLoss(b, s.criterion.finishingWeight)
              : b > s.criterion.startingWeight! &&
                  s.criterion.startingWeight! > s.criterion.middleWeight! &&
                  s.criterion.middleWeight! > s.criterion.finishingWeight;
          }),
          offset + 5,
        );
        const draft = qiDraftExclusionComment(
          `${g}-${String(offset + 6).padStart(2, '0')}`,
          values,
          derived.filter((s) => s.primaryExclusionReason === 'not_recorded').map((s) => s.comment),
        );
        if (draft) set(g, offset + 6, draft);
      }
    } else if (g === 'FMI') {
      const ss = subjects as QiSubject<'FMI'>[];
      const es = reasons(ss, { absent: 2 }) as QiSubject<'FMI'>[];
      count(es, 1);
      if (es.some((s) => !Array.isArray(s.criterion.falls))) fail('fmi');
      else {
        // Manual Part A September 2026 section 12.2: exclude away AND unsupervised.
        // The combined source predicate preserves the rule without inventing either fact.
        const falls = (s: QiSubject<'FMI'>) =>
          s.criterion.falls.filter(
            (f) => inPeriod(f.date) && !(f.awayUnsupervised ?? (f.away && !f.staffSupervised)),
          );
        count(
          es.filter((s) => falls(s).length > 0),
          3,
        );
        count(
          es.filter((s) => falls(s).some((f) => f.majorInjury)),
          4,
        );
        if (
          es.some((s) =>
            s.criterion.falls.some(
              (f) =>
                !Number.isFinite(qiDay(f.date)) ||
                typeof f.majorInjury !== 'boolean' ||
                (f.awayUnsupervised !== undefined
                  ? typeof f.awayUnsupervised !== 'boolean' ||
                    ((f.away !== undefined || f.staffSupervised !== undefined) &&
                      (typeof f.away !== 'boolean' ||
                        typeof f.staffSupervised !== 'boolean' ||
                        f.awayUnsupervised !== (f.away && !f.staffSupervised)))
                  : typeof f.away !== 'boolean' || typeof f.staffSupervised !== 'boolean'),
            ),
          )
        )
          fail('fmi', 'invalid_fall');
      }
    } else if (g === 'MM') {
      const poly = date(group.metadata?.polypharmacyDate, 1, 'polypharmacy');
      const anti = date(group.metadata?.antipsychoticDate, 7, 'antipsychotics');
      if (
        anti &&
        (qiDay(anti) < qiDay(input.periodStart) + 7 ||
          (group.metadata?.windowStart && group.metadata.windowStart !== qiShiftDay(anti, -6)) ||
          (group.metadata?.windowEnd && group.metadata.windowEnd !== anti))
      )
        fail('antipsychotics', 'invalid_collection_window');
      for (const [sub, total, excluded, outcome] of [
        ['polypharmacy', 2, 3, 4],
        ['antipsychotics', 8, 9, 10],
      ] as const) {
        const ss = subjects.filter((s) => s.subgroup === sub) as QiSubject<'MM'>[];
        const es = reasons(ss, { hospital: excluded }) as QiSubject<'MM'>[];
        count(es, total);
        if (sub === 'polypharmacy') {
          if (es.some((s) => !Array.isArray(s.criterion.medicines))) fail(sub);
          else {
            if (
              es.some((s) =>
                s.criterion.medicines!.some(
                  (m) =>
                    !m.medicineKey ||
                    [
                      m.activeIngredient,
                      m.skinOrWoundCare,
                      m.dietarySupplement,
                      m.shortTerm,
                      m.prn,
                    ].some((x) => typeof x !== 'boolean'),
                ),
              )
            )
              fail(sub, 'invalid_medicine');
            count(
              es.filter(
                (s) =>
                  new Set(
                    s.criterion
                      .medicines!.filter(
                        (m) =>
                          m.activeIngredient &&
                          !m.skinOrWoundCare &&
                          !m.dietarySupplement &&
                          !m.shortTerm &&
                          !m.prn,
                      )
                      .map((m) => m.medicineKey),
                  ).size >= 9,
              ),
              outcome,
            );
          }
        } else {
          if (
            es.some(
              (s) =>
                !Array.isArray(s.criterion.administrations) ||
                (typeof s.criterion.psychosis !== 'boolean' && s.criterion.psychosis !== null),
            )
          )
            fail(sub);
          else {
            const start = anti ? qiShiftDay(anti, -6) : undefined;
            const received = es.filter((s) =>
              s.criterion.administrations!.some(
                (m) => m.antipsychotic && start && anti && m.date >= start && m.date <= anti,
              ),
            );
            // Paul ruling, 27 Oct 2026: diagnosis is applicable only after receipt in-window.
            if (received.some((s) => typeof s.criterion.psychosis !== 'boolean'))
              fail(sub, 'psychosis_required_for_antipsychotic');
            count(received, outcome);
            count(
              received.filter((s) => s.criterion.psychosis === true),
              11,
            );
          }
        }
      }
      if (subjects.some((s) => !['polypharmacy', 'antipsychotics'].includes(s.subgroup)))
        fail('*', 'invalid_subgroup');
      void poly;
    } else if (g === 'ADL') {
      const ss = subjects as QiSubject<'ADL'>[];
      const ds = ss.map((s) => ({
        ...s,
        primaryExclusionReason:
          s.primaryExclusionReason ??
          (baseline(s)?.previousAdlTotal == null ? 'not_recorded' : undefined),
      }));
      const es = reasons(ds, { end_of_life: 2, absent: 3, not_recorded: 4 }) as QiSubject<'ADL'>[];
      count(es, 1);
      if (ds.some((s) => s.primaryExclusionReason === 'not_recorded' && !s.comment?.trim()))
        report('missing_exclusion_comment', 'ADL');
      if (
        es.some(
          (s) =>
            s.criterion.total == null ||
            !Number.isInteger(s.criterion.total) ||
            s.criterion.total < 0 ||
            s.criterion.total > 20,
        )
      )
        fail('adl', 'invalid_barthel_total');
      if (
        es.some((s) => {
          const p = baseline(s)?.previousAdlTotal;
          return p == null || !Number.isInteger(p) || p < 0 || p > 20;
        })
      )
        fail('adl', 'invalid_barthel_baseline');
      count(
        es.filter((s) => baseline(s)?.previousAdlTotal === 0),
        5,
      );
      count(
        es.filter((s) => {
          const previous = baseline(s)?.previousAdlTotal;
          return (
            previous != null &&
            previous > 0 &&
            s.criterion.total != null &&
            previous - s.criterion.total >= 1
          );
        }),
        6,
      );
      const draft = qiDraftExclusionComment(
        `${g}-07`,
        values,
        ds.filter((s) => s.primaryExclusionReason === 'not_recorded').map((s) => s.comment),
      );
      if (draft) set(g, 7, draft);
    } else if (g === 'IAD') {
      const ss = subjects as QiSubject<'IAD'>[];
      const es = reasons(ss, { absent: 2 }) as QiSubject<'IAD'>[];
      count(es, 1);
      if (
        es.some(
          (s) =>
            typeof s.criterion.incontinent !== 'boolean' || !Array.isArray(s.criterion.categories),
        )
      )
        fail('iad');
      else {
        count(
          es.filter((s) => !s.criterion.incontinent),
          3,
        );
        const inc = es.filter((s) => s.criterion.incontinent);
        count(inc, 4);
        count(
          inc.filter((s) => s.criterion.categories.length > 0),
          5,
        );
        (['1A', '1B', '2A', '2B'] as const).forEach((c, i) =>
          count(
            inc.filter((s) => s.criterion.categories.includes(c)),
            6 + i,
          ),
        );
        if (
          es.some(
            (s) =>
              (!s.criterion.incontinent && s.criterion.categories.length > 0) ||
              s.criterion.categories.some((c) => !['1A', '1B', '2A', '2B'].includes(c)),
          )
        )
          fail('iad', 'invalid_iad_category');
      }
    } else if (g === 'HP') {
      const ss = subjects as QiSubject<'HP'>[];
      const es = reasons(ss, { absent: 2 }) as QiSubject<'HP'>[];
      count(es, 1);
      if (es.some((s) => !Array.isArray(s.criterion.events))) fail('hp');
      else {
        count(
          es.filter((s) =>
            s.criterion.events.some((e) => inPeriod(e.date) && e.emergencyDepartment),
          ),
          3,
        );
        count(
          es.filter((s) =>
            s.criterion.events.some(
              (e) => inPeriod(e.date) && (e.emergencyDepartment || e.admission),
            ),
          ),
          4,
        );
      }
    } else if (g === 'WF') {
      roles.forEach((role, i) => {
        const ss = subjects.filter((s) => s.subgroup === role) as QiSubject<'WF'>[];
        if (
          ss.some((s) => {
            const b = baseline(s)?.previousStaffHours;
            return (
              b == null ||
              !Number.isFinite(b) ||
              b < 0 ||
              !Array.isArray(s.criterion.workedDates) ||
              s.criterion.workedDates.some((d) => !Number.isFinite(qiDay(d)))
            );
          })
        ) {
          fail(role, 'missing_workforce_baseline_or_dates');
          return;
        }
        count(
          ss.filter((s) => baseline(s)!.previousStaffHours! > 0),
          1 + i,
        );
        const employed = ss.filter((s) => baseline(s)!.previousStaffHours! >= 120);
        count(employed, 5 + i);
        count(
          employed.filter((s) => {
            const days = [...new Set(s.criterion.workedDates.filter(inPeriod).map(qiDay))].sort(
              (a, b) => a - b,
            );
            // Inclusive reporting period: sentinel days preserve 60-day gaps at either edge.
            const points = [qiDay(input.periodStart) - 1, ...days, qiDay(input.periodEnd) + 1];
            return points.some((d, j) => j > 0 && d - points[j - 1]! - 1 >= 60);
          }),
          9 + i,
        );
      });
      if (subjects.some((s) => !roles.includes(s.subgroup as (typeof roles)[number])))
        fail('*', 'invalid_workforce_role');
    } else if (g === 'CEI' || g === 'QOL') {
      const ss = subjects as (QiSubject<'CEI'> | QiSubject<'QOL'>)[];
      const es = reasons(ss, { absent: 2, opted_out: 3 }) as typeof ss;
      // One primary exclusion per person, even if records exist for several survey modes.
      for (const id of new Set(ss.map((s) => s.subjectId))) {
        const rs = ss.filter((s) => s.subjectId === id);
        if (new Set(rs.map((s) => s.primaryExclusionReason ?? 'eligible')).size > 1)
          fail(g.toLowerCase(), 'conflicting_primary_exclusion');
      }
      if (
        es.some(
          (s) =>
            s.criterion.score == null ||
            !Number.isInteger(s.criterion.score) ||
            s.criterion.score < 0 ||
            s.criterion.score > 24 ||
            !modes.includes(s.mode!),
        )
      )
        fail(g.toLowerCase(), 'invalid_survey_score_or_mode');
      count(
        es.filter((s) => s.criterion.score != null),
        1,
      );
      modes.forEach((mode, m) =>
        [22, 19, 14, 8, 0].forEach((lower, b) => {
          const upper = [24, 21, 18, 13, 7][b]!;
          count(
            es.filter(
              (s) =>
                s.mode === mode &&
                s.criterion.score != null &&
                s.criterion.score >= lower &&
                s.criterion.score <= upper,
            ),
            4 + m * 5 + b,
          );
        }),
      );
    } else if (g === 'AH') {
      const ss = subjects as QiSubject<'AH'>[];
      const es = reasons(ss, { absent: 2 }) as QiSubject<'AH'>[];
      count(es, 1);
      for (const id of new Set(ss.map((s) => s.subjectId)))
        if (
          new Set(
            ss.filter((s) => s.subjectId === id).map((s) => s.primaryExclusionReason ?? 'eligible'),
          ).size > 1
        )
          fail('ah', 'conflicting_primary_exclusion');
      if (
        es.some(
          (s) =>
            !disciplines.includes(s.discipline!) ||
            typeof s.criterion.recommended !== 'boolean' ||
            typeof s.criterion.received !== 'boolean',
        )
      )
        fail('ah', 'invalid_allied_health_record');
      // §23.3 unit is resident × discipline, never number of visits.
      const recommended = es.filter((s) => s.criterion.recommended),
        received = recommended.filter((s) => s.criterion.received);
      set(g, 3, new Set(recommended.map((s) => `${s.subjectId}|${s.discipline}`)).size);
      set(g, 11, new Set(received.map((s) => `${s.subjectId}|${s.discipline}`)).size);
      disciplines.forEach((d, i) => {
        count(
          recommended.filter((s) => s.discipline === d),
          4 + i,
        );
        count(
          received.filter((s) => s.discipline === d),
          12 + i,
        );
      });
    }
    for (const f of fs)
      if (
        invalid.has('*') ||
        invalid.has(f.subgroup) ||
        (invalid.size > 0 && !['UPWL', 'MM', 'WF'].includes(g))
      )
        values[f.code] = missingQi('invalid_or_incomplete_subject_input');
    if (g === 'WF' && invalid.size > 0)
      for (const f of fs) values[f.code] = missingQi('invalid_or_incomplete_subject_input');
  }
  // I-1: a resident absent for the whole quarter in one group but recorded present in
  // another whole-quarter group needs a reasoned lock override (Manual Part A September 2026:
  // "absent ... throughout the reporting period" applies to the person, not the indicator).
  const absentAll = new Set<string>();
  const presentIn = new Map<string, QiGroup[]>();
  // CEI, QOL and AH are whole-quarter too (Manual §§19.3.2, 20.3.2, 23.3.2; Paul, 27 Oct).
  for (const g of ['PI', 'FMI', 'ADL', 'IAD', 'HP', 'CEI', 'QOL', 'AH'] as const) {
    const group = input.groups[g];
    if (!group || group.mode !== 'SUBJECT' || !group.collected) continue;
    for (const s of group.subjects) {
      if (s.primaryExclusionReason === 'absent') absentAll.add(s.subjectId);
      else if (!s.primaryExclusionReason)
        presentIn.set(s.subjectId, [...(presentIn.get(s.subjectId) ?? []), g]);
    }
  }
  const inconsistent = new Set<QiGroup>();
  for (const id of absentAll) for (const g of presentIn.get(id) ?? []) inconsistent.add(g);
  for (const g of inconsistent) report('absence_inconsistent_across_groups', g, 'POLICY');
  issues.push(...validateQiValues(values));
  return { values, issues };
}
